import { useEffect, useMemo, useRef, useState } from "react";
import { stills, films } from "@/config/media";
import { useCapabilities } from "@/lib/device";
import useIosSafeVideo from "@/lib/useIosSafeVideo";
import {
  registerMember,
  unregisterMember,
  reportScore,
  grantedSet,
  subscribe,
  isHeroDone,
} from "@/lib/livingRegistry";

let uid = 0;

// The organising component of the site.
// Renders a still. If a film is provided AND the device is capable AND the
// element is within threshold of the viewport centre, requests a grant from
// the global registry; the top three closest requesters get to decode. On
// grant the film loads, plays, and fades in over 700ms above the still. Off
// grant it pauses and fades back to the still. Never loads on mobile,
// prefers-reduced-motion, or before the hero has finished its first clip.
export default function LivingImage({
  still,
  film,
  sizes = "100vw",
  className = "",
  wrapperClassName = "",
  parallax = false,
  eager = false,
  alt,
  onClick,
  testId,
  children,
  playOnHoverOnly = false,
}) {
  const caps = useCapabilities();
  const canFilm = !!film && caps.ready && caps.webgl && !caps.reduced;
  const still_ = stills[still];
  const id = useMemo(() => `li-${++uid}`, []);
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  useIosSafeVideo(videoRef);
  const [inRange, setInRange] = useState(false);
  const [granted, setGranted] = useState(false);
  const [heroReady, setHeroReady] = useState(isHeroDone());
  const [live, setLive] = useState(false); // true after canplay
  const [hovering, setHovering] = useState(false);

  // hero-ready gate
  useEffect(() => {
    if (heroReady) return;
    const t = setInterval(() => {
      if (isHeroDone()) {
        setHeroReady(true);
        clearInterval(t);
      }
    }, 500);
    return () => clearInterval(t);
  }, [heroReady]);

  // register as member the moment we know we can film
  useEffect(() => {
    if (!canFilm) return;
    registerMember(id);
    return () => unregisterMember(id);
  }, [canFilm, id]);

  // measure our distance from viewport centre on scroll/resize
  useEffect(() => {
    if (!canFilm) return;
    const el = wrapRef.current;
    if (!el) return;
    let raf;
    const measure = () => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const vw = window.innerWidth || 1280;
      const vh = window.innerHeight || 800;
      // 2D intersection — a horizontally-scrolled tile that has moved off
      // the left/right edge is not near, even if its vertical rect still
      // sits inside the viewport. Without this, a horizontal carousel would
      // starve its rightmost tiles of grants.
      const near =
        r.bottom > 0 && r.top < vh &&
        r.right > 0 && r.left < vw &&
        r.width > 40 && r.height > 40;
      // 2D centre distance so a tile that is horizontally further from the
      // viewport centre naturally gets a lower priority.
      const dx = cx - vw / 2;
      const dy = cy - vh / 2;
      const dist = Math.hypot(dx, dy);
      setInRange(near);
      if (!hoveringRef.current) reportScore(id, near ? dist : Infinity);
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      reportScore(id, Infinity);
    };
  }, [canFilm, id]);

  // Hover priority — a hovered card is what the user is looking at right
  // now; the registry must not evict it for a passive neighbour.
  const hoveringRef = useRef(false);
  useEffect(() => {
    hoveringRef.current = hovering;
    if (!canFilm) return;
    if (hovering) reportScore(id, -1e6);
    // else: the measure loop will restore the distance-based score on the
    // next scroll frame; no need to force a recompute here.
  }, [canFilm, id, hovering]);

  // listen to global registry — flip granted when we are in the top-3
  useEffect(() => {
    if (!canFilm) return;
    const check = () => setGranted(grantedSet().has(id));
    check();
    return subscribe(check);
  }, [canFilm, id]);

  // effective decision: play only when granted, in range, hero done, and (if
  // hover-only) hovering
  const shouldPlay =
    canFilm && heroReady && granted && inRange && (!playOnHoverOnly || hovering);

  // control the underlying <video>
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (shouldPlay) v.play().catch(() => {});
    else v.pause();
  }, [shouldPlay]);

  const imgClass = `absolute inset-0 h-full w-full object-cover ${className}${
    parallax ? " glide" : ""
  }`;

  const rootPos = wrapperClassName.includes("absolute") ? "" : "relative";
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      ref={wrapRef}
      onClick={onClick}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className={`${rootPos} overflow-hidden ${wrapperClassName}`}
      data-testid={testId}
      {...(onClick ? { type: "button" } : {})}
    >
      <picture>
        <source
          type="image/webp"
          srcSet={`${still_.base}-800.webp 800w, ${still_.base}-1600.webp 1600w`}
          sizes={sizes}
        />
        <img
          src={`${still_.base}-1600.jpg`}
          srcSet={`${still_.base}-800.jpg 800w, ${still_.base}-1600.jpg 1600w`}
          sizes={sizes}
          alt={alt ?? still_.alt}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          className={imgClass}
        />
      </picture>
      {canFilm && (granted || live) && (
        <video
          ref={videoRef}
          src={granted || live ? films[film] : undefined}
          muted
          autoPlay
          loop
          playsInline
          preload="auto"
          onCanPlay={() => setLive(true)}
          aria-hidden="true"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out ${className}`}
          style={{ opacity: shouldPlay && live ? 1 : 0 }}
          data-testid={testId ? `${testId}-video` : undefined}
        />
      )}
      {children}
    </Wrapper>
  );
}
